/**
 * Where the login lives (web-plan.md R44).
 *
 * The JWT sits in localStorage *and* is mirrored into IndexedDB — the second
 * copy exists because the Service Worker (D47) authorizes `<img>`/`<video>`
 * requests and a worker cannot read localStorage. The XSS posture is the
 * plan's: a strict CSP (no inline script, `wasm-unsafe-eval` only for wasm)
 * is the defence; the storage choice does not pretend to be one.
 *
 * One 401 nuance is load-bearing (R44/R50): after a password change the
 * gateway revokes older tokens and the chat socket must re-auth with the
 * fresh JWT — that one 401 bypasses the generic "401 ⇒ logout" path. The
 * bypass is `expectReauth`, armed by the password-change flow.
 */

const KEY = "echofloat.session";
const DB = "echofloat";
const STORE = "session";

export interface Session {
  jwt: string;
  userId: number;
  /** The gateway origin; same-origin in production (D46), so "" — but a dev
   * override survives here for the odd cross-stand session. */
  base: string;
}

let current: Session | null = null;
let reauthWindowUntil = 0;
let revision = 0;

export function session(): Session | null {
  if (current) return current;
  const raw = localStorage.getItem(KEY);
  if (!raw) return null;
  try {
    current = JSON.parse(raw) as Session;
  } catch {
    localStorage.removeItem(KEY);
    return null;
  }
  return current;
}

export function setSession(next: Session | null): void {
  current = next;
  revision += 1;
  if (!next) reauthWindowUntil = 0;
  if (next) {
    localStorage.setItem(KEY, JSON.stringify(next));
  } else {
    localStorage.removeItem(KEY);
  }
  void mirrorToWorker(next);
}

/** The password-change choreography (R44): the next 401 on the socket is the
 * revocation doing its job, not a reason to log out. */
export function armReauthWindow(ms = 15_000): void {
  reauthWindowUntil = Date.now() + ms;
}

export function inReauthWindow(): boolean {
  return Date.now() < reauthWindowUntil;
}

/** The Service Worker's copy (D47): `{jwt}` in a one-row store. Best-effort —
 * a browser that blocks IndexedDB only costs itself the media cache. */
async function mirrorToWorker(next: Session | null): Promise<void> {
  try {
    const expectedRevision = revision;
    const db = await openDb();
    if (revision !== expectedRevision) {
      db.close();
      return;
    }
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    if (next) {
      store.put({ id: 1, jwt: next.jwt });
    } else {
      store.delete(1);
    }
    await done(tx);
    db.close();
  } catch {
    // The SW will 401 and the feed shows broken images — visible, recoverable.
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexeddb refused"));
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("indexeddb tx failed"));
    tx.onabort = () => reject(tx.error ?? new Error("indexeddb tx aborted"));
  });
}
